'use client';

import { useRef, useState, useCallback } from 'react';
import { AlertTriangle, ExternalLink, Maximize2, Pause, Play, RotateCcw, Volume2, VolumeX } from 'lucide-react';
import { Button } from './button.js';

interface SimpleVideoPlayerProps {
  videoSrc: string;
  thumbnail?: string;
  className?: string;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  useNativeControls?: boolean;
  /**
   * Accessible name for the media element. Always pass one for a testimonial
   * or any other video that carries meaning.
   */
  label?: string;
}

/**
 * Simple, robust video player using native HTML5 video element.
 * Uses browser's built-in controls for maximum compatibility.
 * Avoids complex state management that can cause playback issues.
 */
export const SimpleVideoPlayer = ({
  videoSrc,
  thumbnail,
  className = '',
  autoPlay = false,
  muted = true,
  loop = false,
  useNativeControls = false,
  label
}: SimpleVideoPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [isMuted, setIsMuted] = useState(muted);
  const [showControls, setShowControls] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Simple play/pause toggle
  const togglePlayPause = useCallback(() => {
    if (!videoRef.current) return;
    
    if (videoRef.current.paused) {
      videoRef.current.play().catch(err => {
        console.error('Failed to play video:', err);
      });
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  }, []);

  // Simple mute toggle
  const toggleMute = useCallback(() => {
    if (!videoRef.current) return;
    // Drive the DOM from state, not the other way round. The previous version
    // flipped `video.muted` and then stored the NEGATION of the new value, so
    // the controlled `muted={isMuted}` prop immediately undid the toggle on the
    // next render and the mute button did nothing.
    setIsMuted((previous) => !previous);
  }, []);

  // Simple fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (!videoRef.current) return;
    
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(err => {
        console.error('Failed to exit fullscreen:', err);
      });
    } else {
      videoRef.current.requestFullscreen().catch(err => {
        console.error('Failed to enter fullscreen:', err);
      });
    }
  }, []);

  // Update playing state when video plays/pauses
  const handlePlay = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  return (
    <div
      className={`relative group bg-black rounded-lg overflow-hidden shadow-lg ${className}`}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
    >
      {/* Native HTML5 Video Element - Most Compatible */}
      <video
        ref={videoRef}
        src={videoSrc}
        poster={thumbnail}
        className="w-full h-full object-cover"
        muted={isMuted}
        loop={loop}
        playsInline
        controls={useNativeControls}
        preload="metadata"
        aria-label={label}
        onPlay={handlePlay}
        onPause={handlePause}
        onError={() => setHasError(true)}
        onLoadedData={() => setHasError(false)}
      />

      {/*
        A media element that fails to load leaves the poster on screen with a
        play control that silently does nothing — which is exactly how the
        auth-gated `/videos/*.mp4` failure presented in production. Surface it,
        with the two actions that actually help (per ERROR_STATES_ACTIONABLE).
      */}
      {hasError && (
        <div
          role="alert"
          data-testid="video-error"
          className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/85 p-4 text-center text-white"
        >
          <AlertTriangle className="h-6 w-6" aria-hidden="true" />
          <p className="text-sm">This video could not be loaded.</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => {
                setHasError(false);
                videoRef.current?.load();
              }}
            >
              <RotateCcw className="mr-1 h-3 w-3" aria-hidden="true" />
              Try again
            </Button>
            <Button type="button" size="sm" variant="secondary" asChild>
              <a href={videoSrc} target="_blank" rel="noopener noreferrer">
                Open video
                <ExternalLink className="ml-1 h-3 w-3" aria-hidden="true" />
              </a>
            </Button>
          </div>
        </div>
      )}

      {/* Custom Controls - Only show if not using native controls */}
      {!useNativeControls && (
        <>
          {/* Play/Pause Button Overlay */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <Button
              size="icon"
              variant="ghost"
              className={`w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 hover:bg-white/30 transition-all duration-200 text-white hover:text-white pointer-events-auto ${
                isPlaying ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'
              }`}
              onClick={togglePlayPause}
            >
              {isPlaying ? (
                <Pause className="w-8 h-8" />
              ) : (
                <Play className="w-8 h-8 ml-1" />
              )}
            </Button>
          </div>

          {/* Control Bar - Appears on Hover */}
          <div
            className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 transform transition-all duration-200 ${
              showControls ? 'translate-y-0' : 'translate-y-full'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button
                  size="icon"
                  variant="ghost"
                  className="w-8 h-8 text-white hover:bg-white/20"
                  onClick={toggleMute}
                >
                  {isMuted ? (
                    <VolumeX className="w-4 h-4" />
                  ) : (
                    <Volume2 className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <Button
                size="icon"
                variant="ghost"
                className="w-8 h-8 text-white hover:bg-white/20"
                onClick={toggleFullscreen}
              >
                <Maximize2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

